"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import {
  uploadCreativeTemplateAsset,
  generateCreativeTemplate,
  getCreativeTemplateSignedUrl,
} from "@/app/_api/creativeTemplates";
import type { FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { HttpError } from "@utilities/api";
import { useAutoDismissError } from "@/app/_ui/hooks/useAutoDismissError";
import { USER_INPUT_PLACEHOLDER } from "../../constants";
import { buildClaimUrl, toCtaUrl } from "../../ctaUrls";
import type { ContentWorkflowStep, GenerationModel, WorkflowInputConfig } from "../types";
import { createFlowFileValidator, runFlowSubmit, type FlowSubmitForm } from "./runFlowSubmit";

interface GenerateOutcome {
  resultUrl: string;
  resultAssetId: string;
  modelId: string | null;
  refinedPrompt: string | null;
}

function buildRedirectUrl(redirectUrlTemplate: string, assetId?: string, userInput?: string): string {
  const url = new URL(redirectUrlTemplate);
  if (assetId) url.searchParams.set("asset_id", assetId);
  const prompt = url.searchParams.get("prompt");
  if (prompt && userInput) {
    url.searchParams.set("prompt", prompt.replaceAll(USER_INPUT_PLACEHOLDER, userInput));
  }
  return url.toString();
}

export interface UseContentWorkflowReturn {
  step: ContentWorkflowStep;
  userInput: string | null;
  resultUrl: string | null;
  redirectCtaUrl: string | null;
  inputPreviewUrl: string | null;
  selectedFile: File | null;
  error: string | null;
  validateFile: (file: File) => string | null;
  handleFileSelected: (file: File) => void;
  handleTextSubmit: (text: string) => void;
  handleSubmit: (userInput?: string) => void;
  handleRemoveFile: () => void;
  handleFlowSubmit: (form: {
    files: Record<string, File>;
    texts: Record<string, string>;
    prefilledAssetsByPort: Record<string, { assetId: string; previewUrl: string }>;
  }) => void;
  restart: () => void;
}

export function useCreativeTemplate({
  templateSlug,
  templateCategory,
  variantSlug,
  generationModel,
  config,
  redirectUrlTemplate,
  promptRequiresInput = false,
  flowTemplate,
}: {
  templateSlug: string;
  templateCategory: string;
  variantSlug: string;
  generationModel: GenerationModel;
  config: WorkflowInputConfig;
  redirectUrlTemplate: string;
  promptRequiresInput?: boolean;
  flowTemplate?: FlowTemplateSchema | null;
}): UseContentWorkflowReturn {
  const [step, setStep] = useState<ContentWorkflowStep>("idle");
  const [userInput, setUserInput] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [redirectCtaUrl, setRedirectCtaUrl] = useState<string | null>(null);
  const [inputPreviewUrl, setInputPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { error, setError } = useAutoDismissError();

  const t = useTranslations("ClientKeys.creative_templates");
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputPreviewUrlRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (inputPreviewUrlRef.current) {
      URL.revokeObjectURL(inputPreviewUrlRef.current);
      inputPreviewUrlRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Fast pre-upload size guard (the xi API enforces the real limit too). Prefer
  // the per-workflow CMS limit; flow templates carry no CMS size config, so fall
  // back to a safe default. A limit of 0 disables the check entirely.
  const maxFileSizeMb = config.maxFileSizeMb > 0 ? config.maxFileSizeMb : flowTemplate ? 20 : 0;
  const validateFile = useMemo(() => createFlowFileValidator({ maxFileSizeMb, t }), [maxFileSizeMb, t]);

  const executeWorkflow = useCallback(
    async ({ file, prompt }: { file?: File; prompt?: string }) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      let assetId: string | undefined;
      setUserInput(prompt ?? null);

      captureEvent(EventName.mw_creative_template_omnibox_generation_started, {
        template_category: templateCategory,
        variant_slug: variantSlug,
        input_type: file ? "file" : "text",
        generation_model: generationModel,
      });

      try {
        // Upload step (for file inputs)
        if (file) {
          setStep("uploading");
          const uploadResult = await uploadCreativeTemplateAsset(file, abortController.signal);
          assetId = uploadResult.asset_id;

          if (abortController.signal.aborted) return;
        }

        // Video templates require an account — skip the generate call entirely
        // to avoid counting against the global daily rate limit for image generation.
        if (config.resultType === "video") {
          captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
            template_category: templateCategory,
            variant_slug: variantSlug,
            error_type: "rate_limited",
          });
          setRedirectCtaUrl(toCtaUrl(buildRedirectUrl(redirectUrlTemplate, assetId, prompt)));
          setStep("rate_limited");
          return;
        }

        setStep("generating");

        const generateResult = await generateCreativeTemplate({
          templateSlug,
          assetId,
          userInput: prompt,
          signal: abortController.signal,
        });
        const resultAssetId = generateResult.asset_id;
        if (!resultAssetId) {
          throw new Error("No asset ID returned from generation");
        }
        const { url } = await getCreativeTemplateSignedUrl(resultAssetId, abortController.signal);
        const outcome: GenerateOutcome = {
          resultUrl: url,
          resultAssetId,
          modelId: generateResult.model_id,
          refinedPrompt: generateResult.refined_prompt,
        };

        if (abortController.signal.aborted) return;

        captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, {
          template_category: templateCategory,
          variant_slug: variantSlug,
          input_type: file ? "file" : "text",
          generation_model: generationModel,
        });

        setRedirectCtaUrl(
          toCtaUrl(
            buildClaimUrl({
              assetId: outcome.resultAssetId,
              modelId: outcome.modelId,
              refinedPrompt: outcome.refinedPrompt ?? prompt,
            }),
          ),
        );
        setResultUrl(outcome.resultUrl);
        setStep("result");
      } catch (err) {
        if (abortController.signal.aborted) return;

        const errorType = err instanceof HttpError ? err.errorType : null;

        switch (errorType) {
          case "image_too_large":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t("error_image_too_large"));
            setStep("file_selected");
            break;

          case "generation_refused":
          case "cms_unreachable":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t(errorType === "generation_refused" ? "error_generation_refused" : "error_cms_unreachable"));
            setStep(file ? "file_selected" : "idle");
            break;

          case "quota_exceeded":
          case "rate_limited": {
            captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });

            const refinedPrompt =
              err instanceof HttpError ? (err.data?.refined_prompt as string | undefined) : undefined;
            const redirectUrl = new URL(buildRedirectUrl(redirectUrlTemplate, assetId, prompt));
            if (refinedPrompt) redirectUrl.searchParams.set("prompt", refinedPrompt);
            setRedirectCtaUrl(toCtaUrl(redirectUrl.toString()));

            setStep(errorType === "rate_limited" ? "rate_limited" : "quota_exceeded");
            break;
          }

          default:
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_message: err instanceof Error ? err.message : "Unknown error",
              generation_model: generationModel,
            });
            setError(t("error_generic"));
            setStep("idle");
        }
      }
    },
    [templateSlug, templateCategory, variantSlug, generationModel, redirectUrlTemplate, config.resultType, setError, t],
  );

  const handleFileSelected = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);

      // Create preview URL
      if (inputPreviewUrlRef.current) {
        URL.revokeObjectURL(inputPreviewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      inputPreviewUrlRef.current = previewUrl;
      setInputPreviewUrl(previewUrl);

      setSelectedFile(file);

      if (promptRequiresInput) {
        setStep("file_selected");
      } else {
        executeWorkflow({ file });
      }
    },
    [promptRequiresInput, executeWorkflow, validateFile, setError],
  );

  const handleSubmit = useCallback(
    (userInput?: string) => {
      if (selectedFile) {
        executeWorkflow({ file: selectedFile, prompt: userInput });
      }
    },
    [selectedFile, executeWorkflow],
  );

  const handleRemoveFile = useCallback(() => {
    if (inputPreviewUrlRef.current) {
      URL.revokeObjectURL(inputPreviewUrlRef.current);
      inputPreviewUrlRef.current = null;
    }
    setSelectedFile(null);
    setInputPreviewUrl(null);
    setStep("idle");
    setError(null);
  }, [setError]);

  const handleTextSubmit = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      setError(null);
      executeWorkflow({ prompt: text.trim() });
    },
    [executeWorkflow, setError],
  );

  const handleFlowSubmit = useCallback(
    async (form: FlowSubmitForm) => {
      if (!flowTemplate) return;
      const template = flowTemplate;

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setError(null);

      await runFlowSubmit({
        form,
        flowTemplate: template,
        resultType: config.resultType,
        signal: abortController.signal,
        t,
        setStep,
        setError,
        setResultUrl,
        setRedirectCtaUrl,
        toCtaUrl,
        // Hero-media bookkeeping the standalone block doesn't need: keep a
        // "this is what I sent" backdrop visible under the running pill, and seed
        // the omnibox text. Runs synchronously before the "started" event.
        onStart: submittedForm => {
          const firstFile = Object.entries(submittedForm.files)[0]?.[1];
          const firstText = Object.values(submittedForm.texts).find(v => v.trim().length > 0);
          const firstPrefilledAsset = Object.values(submittedForm.prefilledAssetsByPort)[0];

          // Only meaningful when the flow has a single input port — there's then
          // a canonical image to keep visible. Multi-input flows have no obvious
          // pick, so leave preview null and let DynamicHeroMedia render the
          // striped loader instead of an arbitrary first-port image.
          const isSingleInputPort = template.input_ports.length === 1;
          if (!isSingleInputPort) {
            if (inputPreviewUrlRef.current) {
              URL.revokeObjectURL(inputPreviewUrlRef.current);
              inputPreviewUrlRef.current = null;
            }
            setInputPreviewUrl(null);
            setSelectedFile(null);
          } else if (firstFile) {
            if (inputPreviewUrlRef.current) URL.revokeObjectURL(inputPreviewUrlRef.current);
            const previewUrl = URL.createObjectURL(firstFile);
            inputPreviewUrlRef.current = previewUrl;
            setInputPreviewUrl(previewUrl);
            setSelectedFile(firstFile);
          } else if (firstPrefilledAsset) {
            if (inputPreviewUrlRef.current) {
              URL.revokeObjectURL(inputPreviewUrlRef.current);
              inputPreviewUrlRef.current = null;
            }
            setInputPreviewUrl(firstPrefilledAsset.previewUrl);
          }
          setUserInput(firstText ?? null);
        },
        analytics: {
          onStarted: ({ input_type }) =>
            captureEvent(EventName.mw_creative_template_omnibox_generation_started, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              input_type,
              generation_model: generationModel,
            }),
          onSucceeded: ({ input_type }) =>
            captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              input_type,
              generation_model: generationModel,
            }),
          // The video early-redirect intentionally omits `generation_model` (no
          // generation happens); the caught-error quota keeps it — `phase` tells
          // them apart so this stays byte-identical to the pre-refactor events.
          onQuotaExceeded: ({ error_type, phase }) =>
            captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type,
              ...(phase === "error" ? { generation_model: generationModel } : {}),
            }),
          onFailed: ({ error_type, error_message }) =>
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              ...(error_type ? { error_type } : {}),
              ...(error_message ? { error_message } : {}),
              generation_model: generationModel,
            }),
        },
      });
    },
    [flowTemplate, templateCategory, variantSlug, generationModel, config.resultType, setError, t],
  );

  const restart = useCallback(() => {
    cleanup();
    setStep("idle");
    setResultUrl(null);
    setRedirectCtaUrl(null);
    setInputPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
  }, [cleanup, setError]);

  return {
    step,
    userInput,
    resultUrl,
    redirectCtaUrl,
    inputPreviewUrl,
    selectedFile,
    error,
    validateFile,
    handleFileSelected,
    handleTextSubmit,
    handleSubmit,
    handleRemoveFile,
    handleFlowSubmit,
    restart,
  };
}
